// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { HistoricalBalances } from '@shared/data-access/types/brokerage-types';
import { AppIcon, ChartContainer } from '@shared/ui-kit/components';
import {
  classNames,
  formatDate,
  formatToMoney,
  formatToPercent,
  hexToRgba,
} from '@shared/ui-kit/utils';
import { isUndefined } from '@shared/utils';
import { Menu, MenuItem } from '@mui/material';
import {
  ColorType,
  createChart,
  LineStyle,
  LineType,
  type AreaData,
  type IChartApi,
} from 'lightweight-charts';
import { useEffect, useRef, useState, type FC } from 'react';
import {
  type SeriesDataKeys,
  useHistoricalBalancesChart,
} from './use-historical-balances-chart';
import { useTranslation } from 'react-i18next';

interface TooltipData {
  value: string;
  label: string;
  date: string;
}

export interface HistoricalBalancesChartProps {
  data: HistoricalBalances;
  currency: string;
  fixedSerie?: SeriesDataKeys;
  onActiveSerieChange?: (serie: SeriesDataKeys) => void;
}

export const HistoricalBalancesChart: FC<HistoricalBalancesChartProps> = ({
  data,
  currency,
  fixedSerie,
  onActiveSerieChange,
}) => {
  const { t, i18n } = useTranslation();
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi>();
  const [tooltipValues, setTooltipValues] = useState<TooltipData>();
  const [headerMenuAnchorEl, setHeaderMenuAnchorEl] = useState<HTMLElement | null>(
    null,
  );

  const { activeSerie, onChangeSerie, selectorOptions, metrics, isFixedMode } =
    useHistoricalBalancesChart(data, fixedSerie);

  const isHeaderMenuOpen = headerMenuAnchorEl !== null;

  useEffect(() => {
    onActiveSerieChange?.(activeSerie.id);
  }, [activeSerie.id, onActiveSerieChange]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    chart.current = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#edf0f9' },
        textColor: '#515F70',
        fontFamily: 'Poppins',
        fontSize: 12,
      },
      width: chartContainerRef.current!.clientWidth,
      height: Math.max(chartContainerRef.current!.clientHeight, 228),
      handleScroll: false,
      handleScale: false,
      grid: {
        vertLines: {
          visible: false,
        },
        horzLines: {
          style: LineStyle.LargeDashed,
        },
      },
      crosshair: {
        horzLine: { visible: false, labelVisible: false },
        vertLine: { labelVisible: false },
      },
      rightPriceScale: {
        borderVisible: false,
        scaleMargins: {
          bottom: 0,
        },
      },
      timeScale: {
        allowBoldLabels: false,
        borderVisible: false,
      },
    });

    const areaSerie = chart.current.addAreaSeries({
      lineColor: activeSerie.color,
      topColor: activeSerie.color,
      bottomColor: hexToRgba(activeSerie.color, 0.3),
      lineType: LineType.Simple,
      lineWidth: 2,
      crosshairMarkerVisible: false,
      priceFormat: { type: 'volume' },
    });
    areaSerie.setData(activeSerie.data);

    chart.current.timeScale().fitContent();

    /* istanbul ignore next */
    chart.current.subscribeCrosshairMove(param => {
      if (isUndefined(param.point) || !param.time) {
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }
      } else {
        const serie = param.seriesData.get(areaSerie) as AreaData;
        const data: TooltipData = {
          value: formatToMoney(serie.value, currency),
          label: activeSerie.label,
          date: formatDate(serie.time as string, 'medium', i18n.language) ?? '',
        };
        setTooltipValues(data);

        if (!tooltipRef.current || !chart.current) return;

        const container = chartContainerRef.current!;
        const toolTipWidth = tooltipRef.current.clientWidth;
        const toolTipHeight = tooltipRef.current.clientHeight;
        const toolTipMargin = 15;
        const y = param.point.y;
        let left = param.point.x + toolTipMargin;
        if (left > container.clientWidth - toolTipWidth) {
          left = param.point.x - toolTipMargin - toolTipWidth;
        }
        let top = y + toolTipMargin;
        if (top > container.clientHeight - toolTipHeight) {
          top = y - toolTipHeight - toolTipMargin;
        }

        tooltipRef.current.style.left = left + 'px';
        tooltipRef.current.style.top = top + 'px';
        tooltipRef.current.style.display = 'block';
      }
    });

    resizeObserver.current = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      chart.current?.applyOptions({
        width,
        height: Math.max(height, 228),
      });
      chart.current?.timeScale().fitContent();
    });

    resizeObserver.current.observe(chartContainerRef.current);

    return () => {
      resizeObserver.current?.disconnect();
      chart.current?.remove();
    };
  }, [activeSerie, currency, i18n.language]);

  return (
    <ChartContainer
      header={
        isFixedMode ? (
          activeSerie.label
        ) : (
          <button
            type="button"
            className="flex items-center gap-2 bg-transparent p-0 text-title-dark"
            onClick={event => setHeaderMenuAnchorEl(event.currentTarget)}
            data-testid="historical-balances-header-series-selector"
          >
            <span className="font-medium">{activeSerie.label}</span>
            <AppIcon
              icon="ArrowDown"
              size="lg"
              color="inherit"
              className={classNames('transition-transform', {
                'text-primary-medium': true,
                'rotate-180': isHeaderMenuOpen,
              })}
            />
          </button>
        )
      }
      divider={false}
      fullHeight={true}
    >
      {!isFixedMode && (
        <Menu
          anchorEl={headerMenuAnchorEl}
          open={isHeaderMenuOpen}
          onClose={() => setHeaderMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              className:
                '!mt-1 !rounded-xl !border !border-black/25 !bg-page !shadow-none',
            },
          }}
        >
          {selectorOptions.map(option => (
            <MenuItem
              key={option.value}
              selected={option.value === activeSerie.id}
              className="!text-primary-medium hover:!bg-section data-[selected=true]:!bg-primary/25 data-[selected=true]:!text-primary"
              onClick={() => {
                onChangeSerie(option.value as SeriesDataKeys);
                setHeaderMenuAnchorEl(null);
              }}
            >
              {option.label}
            </MenuItem>
          ))}
        </Menu>
      )}

      <div className="flex w-full shrink-0 flex-col items-start gap-3 md:flex-row md:items-center">
        <div
          data-testid="chart-metrics"
          className="text-base font-semibold text-title-dark"
        >
          <span className="!money-display pr-2 text-2xl font-semibold text-title-dark">
            {formatToMoney(metrics.lastValue, currency)}
          </span>
          <span
            className={classNames(
              {
                'text-positive': metrics.performance > 0,
                'text-accent-coral': metrics.performance <= 0,
              },
              '!money-display',
            )}
          >
            {formatToMoney(metrics.difference, currency)} (
            {formatToPercent(metrics.performance)})
          </span>
        </div>
      </div>
      {activeSerie.data.length > 0 ? (
        <div
          ref={chartContainerRef}
          className="relative mt-3 min-h-[228px] w-full flex-1"
          data-testid="historical-balances-chart"
        >
          {tooltipValues?.date.length && (
            <div
              ref={tooltipRef}
              className="absolute top-4 z-50 min-w-32 rounded-lg bg-white p-2 text-center text-secondary-dark shadow-lg"
            >
              <p className="font-semibold">{tooltipValues?.label}</p>
              <p className="!money-display">{tooltipValues?.value}</p>
              <p>{tooltipValues?.date}</p>
            </div>
          )}
        </div>
      ) : (
        <div
          className="flex min-h-0 flex-1 items-center justify-center font-semibold text-border-dark"
          data-testid="no-data-message"
        >
          <p>{t('common.no-data', { ns: 'common' })}</p>
        </div>
      )}
    </ChartContainer>
  );
};
